import { useMemo, useState } from "react";
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  PaperClipIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { ROOT_API_URL } from "../config/defaultSettings";
import { Button, Modal } from "../shared";

/**
 * @param {Object} props
 * @param {Array} [props.attachments]
 * @param {boolean} [props.isLoading]
 * @param {string} [props.title]
 * @param {string} [props.emptyMessage]
 * @param {Object} [props.jobsById]
 * @param {Function} [props.getJobLabel]
 * @param {Object} [props.legsById]
 * @param {Function} [props.getLegLabel]
 * @param {boolean} [props.openNonImageInModal]
 * @param {string} [props.previewSize]
 * @param {string} [props.previewPanelClassName]
 * @param {string} [props.previewContentWrapperClass]
 * @param {string} [props.previewMaxHeightClass]
 */
export const AttachmentsSidebarList = ({
  attachments = [],
  isLoading = false,
  title = "Attachments",
  emptyMessage = "Files attached will appear here",
  jobsById = null,
  getJobLabel = null,
  legsById = null,
  getLegLabel = null,
  openNonImageInModal = false,
  previewSize = "lg",
  previewPanelClassName = "w-[95vw] max-w-[95vw] sm:max-w-[42rem]",
  previewContentWrapperClass = "p-3 lg:p-4",
  previewMaxHeightClass = "max-h-[70vh] sm:max-h-[80vh]",
} = {}) => {
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [failedAttachmentUrls, setFailedAttachmentUrls] = useState({});

  const imageAttachments = useMemo(
    () =>
      attachments.filter((attachment) =>
        (attachment?.mimeType || attachment?.mimetype || "").startsWith(
          "image/",
        ),
      ),
    [attachments],
  );

  const getApiOrigin = () =>
    String(ROOT_API_URL || "").replace(/\/api\/v1\/?$/, "");

  const stripApiV1 = (value) =>
    String(value || "").replace(/\/api\/v1\/?/, "/");

  const getAttachmentKey = (attachment) =>
    attachment?._id ||
    attachment?.id ||
    attachment?.filename ||
    attachment?.name;

  const buildAttachmentUrl = (attachment, options = {}) => {
    const apiOrigin = getApiOrigin();
    const path = attachment?.path;
    const pathHostMatch = path?.match(/\/home\/[^/]+\/([^/]+)\/uploads\//);
    const candidate = attachment?.url || attachment?.uri;

    if (candidate) {
      if (/^https?:\/\//i.test(candidate)) {
        try {
          const parsed = new URL(stripApiV1(candidate));
          const isLocalhost = ["localhost", "127.0.0.1", "0.0.0.0"].includes(
            parsed.hostname,
          );
          const hostFromPath = pathHostMatch?.[1]
            ? `https://${pathHostMatch[1]}`
            : "";
          const fallbackPath = options.forceUploadsPrefix
            ? parsed.pathname.startsWith("/uploads/")
              ? parsed.pathname
              : `/uploads${parsed.pathname}`
            : parsed.pathname;

          if (!isLocalhost) {
            return `${parsed.origin}${fallbackPath}`.replace(
              /([^:]\/)\/+/g,
              "$1",
            );
          }

          if (hostFromPath) {
            return `${hostFromPath}${fallbackPath}`.replace(
              /([^:]\/)\/+/g,
              "$1",
            );
          }

          if (apiOrigin) {
            return `${apiOrigin}${fallbackPath}`.replace(/([^:]\/)\/+/g, "$1");
          }

          return `${parsed.origin}${fallbackPath}`.replace(
            /([^:]\/)\/+/g,
            "$1",
          );
        } catch (error) {
          return stripApiV1(candidate).replace(/([^:]\/)\/+/g, "$1");
        }
      }

      const normalized = candidate.startsWith("/")
        ? candidate
        : `/${candidate}`;
      const normalizedPath = stripApiV1(normalized);
      const fallbackPath = options.forceUploadsPrefix
        ? normalizedPath.startsWith("/uploads/")
          ? normalizedPath
          : `/uploads${normalizedPath}`
        : normalizedPath;
      return `${apiOrigin}${fallbackPath}`.replace(/([^:]\/)\/+/g, "$1");
    }

    if (path && path.includes("uploads/")) {
      const [, relativePath] = path.split("uploads/");
      if (relativePath) {
        if (pathHostMatch?.[1]) {
          return `https://${pathHostMatch[1]}/${relativePath}`.replace(
            /([^:]\/)\/+/g,
            "$1",
          );
        }
        const basePath = options.forceUploadsPrefix
          ? `uploads/${relativePath}`
          : relativePath;
        return `${apiOrigin}/${basePath}`.replace(/([^:]\/)\/+/g, "$1");
      }
    }

    if (attachment?.dir && attachment?.filename) {
      return `${apiOrigin}/${attachment.dir}/${attachment.filename}`.replace(
        /([^:]\/)\/+/g,
        "$1",
      );
    }

    return "";
  };

  const buildDownloadUrl = (attachment) => {
    if (!attachment?._id) return "";
    const pathHostMatch = attachment?.path?.match(/\/home\/[^/]+\/([^/]+)\//);
    const candidate = attachment?.url || attachment?.uri;
    let apiBase = ROOT_API_URL;

    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        const parsed = new URL(candidate);
        const isLocalhost = ["localhost", "127.0.0.1", "0.0.0.0"].includes(
          parsed.hostname,
        );
        if (!isLocalhost) {
          apiBase = `${parsed.origin}/api/v1`;
        }
      } catch {
        // no-op
      }
    }

    if (pathHostMatch?.[1]) {
      apiBase = `https://${pathHostMatch[1]}/api/v1`;
    }

    return `${apiBase}/media/${attachment._id}/download`.replace(
      /([^:]\/)\/+/g,
      "$1",
    );
  };

  const resolveAttachmentUrl = (attachment) => {
    const attachmentKey = getAttachmentKey(attachment);
    const useFallback = Boolean(failedAttachmentUrls[attachmentKey]);
    return buildAttachmentUrl(attachment, {
      forceUploadsPrefix: useFallback,
    });
  };

  const markAttachmentUrlFailed = (attachment) => {
    const attachmentKey = getAttachmentKey(attachment);
    if (!attachmentKey || failedAttachmentUrls[attachmentKey]) return;
    setFailedAttachmentUrls((prev) => ({ ...prev, [attachmentKey]: true }));
  };

  const handleDownload = async (attachment) => {
    try {
      const downloadUrl =
        buildDownloadUrl(attachment) || resolveAttachmentUrl(attachment);
      if (!downloadUrl) {
        toast.error("No download URL available");
        return;
      }
      const response = await fetch(downloadUrl, { credentials: "include" });
      if (!response.ok) {
        throw new Error("Failed to download file");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename || attachment.name || "attachment";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("File downloaded");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const handleOpenFile = (attachment) => {
    const isImage = (
      attachment?.mimeType ||
      attachment?.mimetype ||
      ""
    ).startsWith("image/");
    if (isImage) {
      const imageIndex = imageAttachments.findIndex(
        (img) =>
          String(getAttachmentKey(img)) ===
          String(getAttachmentKey(attachment)),
      );
      setCurrentImageIndex(imageIndex >= 0 ? imageIndex : 0);
      setPreviewAttachment(attachment);
      setPreviewModalVisible(true);
      return;
    }

    if (openNonImageInModal) {
      setPreviewAttachment(attachment);
      setPreviewModalVisible(true);
      return;
    }

    const resolvedUrl = resolveAttachmentUrl(attachment);
    if (resolvedUrl) {
      window.open(resolvedUrl, "_blank");
    } else {
      toast.error("File URL not available");
    }
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return DocumentTextIcon;
    if (mimeType.startsWith("image/")) return PhotoIcon;
    return DocumentTextIcon;
  };

  const getAttachmentJobId = (attachment) => {
    if (typeof attachment?.job === "string") return attachment.job;
    return attachment?.jobId || attachment?.job?._id || attachment?.job?.id;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "Unknown size";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Loading attachments...</p>
        </div>
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <PaperClipIcon className="h-12 w-12 text-gray-300 mb-3" />
        <p className="text-sm font-medium text-gray-700 mb-1">No attachments</p>
        <p className="text-xs text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            {title} ({attachments.length})
          </h3>
        </div>
        <div className="space-y-2">
          {attachments.map((attachment, index) => {
            const FileIcon = getFileIcon(
              attachment.mimeType || attachment.mimetype || "",
            );
            const isImage = (
              attachment.mimeType ||
              attachment.mimetype ||
              ""
            ).startsWith("image/");
            const fileName =
              attachment.filename || attachment.name || "Unnamed file";
            const fileSize = formatFileSize(attachment.size || 0);
            const resolvedUrl = resolveAttachmentUrl(attachment);
            const attachmentType = attachment?.type || attachment?.refType;
            const leg = legsById?.[String(attachment?.refId || "")];
            const legLabel = getLegLabel ? getLegLabel(leg) : null;
            const jobId = getAttachmentJobId(attachment);
            const job = jobsById?.[String(jobId || "")];
            const resolvedJobLabel =
              typeof getJobLabel === "function"
                ? getJobLabel(job, attachment)
                : job?.jobId || job?._id || jobId;

            return (
              <div
                key={attachment._id || attachment.id || index}
                className="bg-white border border-gray-200 rounded-lg p-3 hover:border-teal-300 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {isImage ? (
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        <img
                          src={resolvedUrl}
                          alt={fileName}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => handleOpenFile(attachment)}
                          onError={() => markAttachmentUrlFailed(attachment)}
                        />
                      </div>
                    ) : (
                      <div
                        className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center cursor-pointer"
                        onClick={() => handleOpenFile(attachment)}
                      >
                        <FileIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-teal-600"
                          onClick={() => handleOpenFile(attachment)}
                          title={fileName}
                        >
                          {fileName}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {fileSize}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {resolvedJobLabel && (
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 uppercase tracking-wide">
                              {resolvedJobLabel}
                            </span>
                          )}
                          {attachmentType && (
                            <span className="inline-flex items-center rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-teal-700 uppercase tracking-wide">
                              {attachmentType}
                            </span>
                          )}
                          {attachment?.refType === "leg" && legLabel && (
                            <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">
                              {legLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleDownload(attachment)}
                      className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                      title="Download file"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Modal
        visible={previewModalVisible}
        setVisible={setPreviewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        size={previewSize}
        hideFooter={true}
        panelClassName={previewPanelClassName}
        contentWrapperClass={previewContentWrapperClass}
      >
        {(previewAttachment?.mimetype?.startsWith("image/") ||
          previewAttachment?.mimeType?.startsWith("image/")) &&
        imageAttachments.length ? (
          <div className="relative">
            <img
              src={buildAttachmentUrl(imageAttachments[currentImageIndex])}
              alt={
                imageAttachments[currentImageIndex]?.filename || "Attachment"
              }
              className={`w-full h-full object-contain ${previewMaxHeightClass}`}
            />
            {imageAttachments.length > 1 && (
              <>
                <button
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                  onClick={() =>
                    setCurrentImageIndex((prev) =>
                      prev === 0 ? imageAttachments.length - 1 : prev - 1,
                    )
                  }
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                  onClick={() =>
                    setCurrentImageIndex((prev) =>
                      prev === imageAttachments.length - 1 ? 0 : prev + 1,
                    )
                  }
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="p-2">
            <p className="text-sm text-gray-600 mb-4">
              {previewAttachment?.filename ||
                "Attachment preview not available"}
            </p>
            <Button
              onClick={() => handleDownload(previewAttachment)}
              type="primary"
              size="sm"
            >
              Download File
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AttachmentsSidebarList;
